import { useState, useEffect, useCallback } from 'react'
import {
  FlaskConical,
  Plus,
  RefreshCw,
  Search,
  X,
  Download,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Trash2,
} from 'lucide-react'
import {
  api,
  type TechEval,
  type TechEvalVerdict,
  type TechEvalStats,
  type TechEvalScores,
} from '../lib/api'

// ─── Constants ───

const VERDICTS: { value: TechEvalVerdict; label: string; color: string; darkColor: string }[] = [
  { value: 'ADOPT', label: 'Adopt', color: 'bg-green-100 text-green-700', darkColor: 'dark:bg-green-900/30 dark:text-green-400' },
  { value: 'TRIAL', label: 'Trial', color: 'bg-blue-100 text-blue-700', darkColor: 'dark:bg-blue-900/30 dark:text-blue-400' },
  { value: 'ASSESS', label: 'Assess', color: 'bg-amber-100 text-amber-700', darkColor: 'dark:bg-amber-900/30 dark:text-amber-400' },
  { value: 'HOLD', label: 'Hold', color: 'bg-gray-100 text-gray-600', darkColor: 'dark:bg-gray-700 dark:text-gray-400' },
  { value: 'REJECT', label: 'Reject', color: 'bg-red-100 text-red-700', darkColor: 'dark:bg-red-900/30 dark:text-red-400' },
]

const DEFAULT_VERDICT_STYLE = { value: 'HOLD' as const, label: 'Hold', color: 'bg-gray-100 text-gray-600', darkColor: 'dark:bg-gray-700 dark:text-gray-400' }

function getVerdictStyle(verdict: string) {
  return VERDICTS.find((v) => v.value === verdict) ?? DEFAULT_VERDICT_STYLE
}

const SCORE_LABELS: Record<string, string> = {
  R: 'REDTAPE',
  D: 'DOCTRINE',
  B: 'BGSc Resell',
  W: 'White Papers',
}

function ScoreBadge({ label, value }: { label: string; value: number }) {
  const bg = value >= 4 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    : value >= 3 ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
    : value >= 2 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
    : 'bg-gray-100 text-gray-500 dark:bg-surface-700 dark:text-gray-500'
  return (
    <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${bg}`} title={`${SCORE_LABELS[label] || label}: ${value}/5`}>
      {label}:{value}
    </span>
  )
}

// ─── Page ───

export function TechEvalsPage() {
  const [evaluations, setEvaluations] = useState<TechEval[]>([])
  const [stats, setStats] = useState<TechEvalStats | null>(null)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [importing, setImporting] = useState(false)

  // Filters
  const [verdictFilter, setVerdictFilter] = useState<string>('')
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')

  // UI
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [editingEval, setEditingEval] = useState<TechEval | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  // Form
  const [formEvalId, setFormEvalId] = useState('')
  const [formName, setFormName] = useState('')
  const [formSlug, setFormSlug] = useState('')
  const [formVerdict, setFormVerdict] = useState<TechEvalVerdict>('ASSESS')
  const [formCategory, setFormCategory] = useState('')
  const [formNotes, setFormNotes] = useState('')
  const [formTags, setFormTags] = useState('')

  // ─── Load ───

  const loadEvals = useCallback(async () => {
    setLoading(true)
    try {
      const filters: Record<string, string> = {}
      if (verdictFilter) filters.verdict = verdictFilter
      if (categoryFilter) filters.category = categoryFilter
      if (searchQuery) filters.search = searchQuery
      filters.limit = '200'
      const result = await api.techEvals.list(filters)
      setEvaluations(result.evaluations)
      setTotal(result.total)
    } catch {
      showToast('Failed to load evaluations', 'error')
    } finally {
      setLoading(false)
    }
  }, [verdictFilter, categoryFilter, searchQuery])

  const loadStats = useCallback(async () => {
    try {
      const s = await api.techEvals.stats()
      setStats(s)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => { loadEvals() }, [loadEvals])
  useEffect(() => { loadStats() }, [loadStats])

  // ─── Actions ───

  function showToast(message: string, type: 'success' | 'error') {
    setToast({ message, type })
    setTimeout(() => setToast(null), 2500)
  }

  async function handleImportAll() {
    setImporting(true)
    try {
      const result = await api.techEvals.importAll()
      const totalImported = result.tracker.imported + result.files.imported
      const totalSkipped = result.tracker.skipped + result.files.skipped
      showToast(`Imported ${totalImported}, skipped ${totalSkipped}`, 'success')
      loadEvals()
      loadStats()
    } catch {
      showToast('Import failed', 'error')
    } finally {
      setImporting(false)
    }
  }

  async function handleDelete(id: string) {
    try {
      await api.techEvals.delete(id)
      showToast('Deleted', 'success')
      loadEvals()
      loadStats()
    } catch {
      showToast('Delete failed', 'error')
    }
  }

  function openCreate() {
    setEditingEval(null)
    setFormEvalId('')
    setFormName('')
    setFormSlug('')
    setFormVerdict('ASSESS')
    setFormCategory('')
    setFormNotes('')
    setFormTags('')
    setShowCreate(true)
    // Fetch next ID
    api.techEvals.nextId().then((r) => setFormEvalId(r.nextId)).catch(() => {})
  }

  function openEdit(ev: TechEval) {
    setEditingEval(ev)
    setFormEvalId(ev.eval_id)
    setFormName(ev.name)
    setFormSlug(ev.slug)
    setFormVerdict(ev.verdict)
    setFormCategory(ev.category)
    setFormNotes(ev.notes || '')
    setFormTags((ev.tags || []).join(', '))
    setShowCreate(true)
  }

  async function saveEval() {
    if (!formName.trim()) return
    const input: any = {
      eval_id: formEvalId,
      name: formName,
      slug: formSlug || formName.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      verdict: formVerdict,
      category: formCategory || 'general',
      notes: formNotes || null,
      tags: formTags ? formTags.split(',').map((t) => t.trim()).filter(Boolean) : [],
    }
    try {
      if (editingEval) {
        await api.techEvals.patch(editingEval.id, input)
        showToast('Updated', 'success')
      } else {
        await api.techEvals.create(input)
        showToast('Created', 'success')
      }
      setShowCreate(false)
      loadEvals()
      loadStats()
    } catch {
      showToast('Save failed', 'error')
    }
  }

  // ─── Unique categories from data ───

  const uniqueCategories = Array.from(new Set(evaluations.map((e) => e.category))).sort()

  // ─── Render ───

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-violet-400 to-violet-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/30">
            <FlaskConical className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              Tech Evaluations
              <span className="text-sm font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 px-2 py-0.5 rounded-full">
                {total}
              </span>
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleImportAll}
            disabled={importing}
            className="px-3 py-1.5 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 flex items-center gap-1"
          >
            <Download className="w-4 h-4" />
            {importing ? 'Importing...' : 'Import All'}
          </button>
          <button
            onClick={openCreate}
            className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-700 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200 flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            New Eval
          </button>
          <button
            onClick={() => { loadEvals(); loadStats() }}
            className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-surface-700"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats Bar - Verdict Buttons */}
      {stats && (
        <div className="grid grid-cols-5 gap-2">
          {VERDICTS.map((v) => {
            const count = stats.byVerdict[v.value] || 0
            const isActive = verdictFilter === v.value
            return (
              <button
                key={v.value}
                onClick={() => setVerdictFilter(isActive ? '' : v.value)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-between
                  ${isActive
                    ? `${v.color} ${v.darkColor} ring-2 ring-offset-1 ring-current`
                    : 'bg-white dark:bg-surface-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-surface-700'
                  }`}
              >
                <span>{v.label}</span>
                <span className="font-bold">{count}</span>
              </button>
            )
          })}
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, ID, or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 dark:border-surface-700 rounded-lg bg-white dark:bg-surface-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1">
          {uniqueCategories.slice(0, 12).map((cat) => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(categoryFilter === cat ? '' : cat)}
              className={`px-2 py-1 text-xs rounded-full transition-all ${
                categoryFilter === cat
                  ? 'bg-violet-600 text-white'
                  : 'bg-gray-100 text-gray-600 dark:bg-surface-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-surface-600'
              }`}
            >
              {cat}
            </button>
          ))}
          {categoryFilter && !uniqueCategories.slice(0, 12).includes(categoryFilter) && (
            <button
              onClick={() => setCategoryFilter('')}
              className="px-2 py-1 text-xs rounded-full bg-violet-600 text-white"
            >
              {categoryFilter} <X className="w-3 h-3 inline" />
            </button>
          )}
        </div>
      </div>

      {/* Evaluation Cards */}
      {loading ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">Loading...</div>
      ) : evaluations.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          No evaluations found. Click "Import All" to seed from tracker data.
        </div>
      ) : (
        <div className="space-y-2">
          {evaluations.map((ev) => {
            const vstyle = getVerdictStyle(ev.verdict)
            const isExpanded = expandedId === ev.id
            const scores = ev.scores as TechEvalScores | null
            return (
              <div
                key={ev.id}
                className="bg-white dark:bg-surface-800 border border-gray-200 dark:border-surface-700 rounded-lg hover:shadow-sm transition-shadow"
              >
                <div className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {/* Verdict badge */}
                    <span className={`px-2 py-0.5 text-xs font-bold rounded shrink-0 ${vstyle.color} ${vstyle.darkColor}`}>
                      {ev.verdict}
                    </span>

                    {/* Eval ID */}
                    <span className="text-xs font-mono text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 px-1.5 py-0.5 rounded shrink-0">
                      {ev.eval_id}
                    </span>

                    {/* Category */}
                    <span className="text-xs text-gray-500 dark:text-gray-500 bg-gray-100 dark:bg-surface-700 px-1.5 py-0.5 rounded shrink-0">
                      {ev.category}
                    </span>

                    {/* Name - clickable to edit */}
                    <button
                      onClick={() => openEdit(ev)}
                      className="font-medium text-gray-900 dark:text-white hover:text-violet-600 dark:hover:text-violet-400 text-left truncate flex-1"
                    >
                      {ev.name}
                    </button>

                    {/* Scores */}
                    {scores && (scores.r != null || scores.d != null) && (
                      <div className="hidden sm:flex items-center gap-1">
                        {scores.r != null && <ScoreBadge label="R" value={scores.r} />}
                        {scores.d != null && <ScoreBadge label="D" value={scores.d} />}
                        {scores.b != null && <ScoreBadge label="B" value={scores.b} />}
                        {scores.w != null && <ScoreBadge label="W" value={scores.w} />}
                        {scores.tech && (
                          <span className="text-xs font-mono text-gray-500 dark:text-gray-500 bg-gray-50 dark:bg-surface-700 px-1.5 py-0.5 rounded" title="Composite tech score">{scores.tech}</span>
                        )}
                      </div>
                    )}

                    {/* Wave */}
                    {ev.wave && (
                      <span className="text-xs text-gray-400 shrink-0">W{ev.wave}</span>
                    )}

                    {/* Expand/Collapse */}
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : ev.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0"
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>

                    {/* Delete */}
                    <button
                      onClick={() => handleDelete(ev.id)}
                      className="p-1 text-gray-300 hover:text-red-500 dark:text-gray-600 dark:hover:text-red-400 shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Description - always visible */}
                  {ev.notes && (
                    <p className="mt-1.5 ml-[4.5rem] text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                      {ev.notes}
                    </p>
                  )}
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-4 pb-3 pt-0 border-t border-gray-100 dark:border-surface-700 space-y-2">
                    {/* Mobile scores (hidden on desktop where they're inline) */}
                    {scores && (scores.r != null || scores.d != null) && (
                      <div className="flex sm:hidden items-center gap-1.5 flex-wrap pt-1">
                        {scores.r != null && <ScoreBadge label="R" value={scores.r} />}
                        {scores.d != null && <ScoreBadge label="D" value={scores.d} />}
                        {scores.b != null && <ScoreBadge label="B" value={scores.b} />}
                        {scores.w != null && <ScoreBadge label="W" value={scores.w} />}
                        {scores.tech && (
                          <span className="text-xs font-mono text-gray-500 dark:text-gray-500 bg-gray-50 dark:bg-surface-700 px-1.5 py-0.5 rounded">{scores.tech}</span>
                        )}
                      </div>
                    )}
                    {ev.eval_url && (
                      <div className="flex items-center gap-1 text-sm">
                        <ExternalLink className="w-3.5 h-3.5 text-violet-500" />
                        <span className="text-gray-500 dark:text-gray-500 font-mono text-xs truncate">{ev.eval_url}</span>
                      </div>
                    )}
                    {ev.tags && ev.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {ev.tags.map((tag) => (
                          <span key={tag} className="px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500 dark:bg-surface-700 dark:text-gray-400">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {ev.artifacts && ev.artifacts.length > 0 && (
                      <div className="text-xs text-gray-400">
                        {ev.artifacts.length} artifact{ev.artifacts.length !== 1 ? 's' : ''}
                        {ev.artifacts.map((a) => (
                          <div key={a} className="font-mono truncate ml-2">{a}</div>
                        ))}
                      </div>
                    )}
                    {ev.duration && (
                      <div className="text-xs text-gray-400">Duration: {ev.duration}</div>
                    )}
                    {ev.evaluated_at && (
                      <div className="text-xs text-gray-400">
                        Evaluated: {new Date(ev.evaluated_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowCreate(false)}>
          <div className="bg-white dark:bg-surface-800 rounded-xl shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              {editingEval ? 'Edit Evaluation' : 'New Evaluation'}
            </h2>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Eval ID</label>
                  <input
                    type="text"
                    value={formEvalId}
                    onChange={(e) => setFormEvalId(e.target.value)}
                    placeholder="TE-XX"
                    disabled={!!editingEval}
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-surface-600 rounded-lg bg-white dark:bg-surface-700 text-gray-900 dark:text-white disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Verdict</label>
                  <select
                    value={formVerdict}
                    onChange={(e) => setFormVerdict(e.target.value as TechEvalVerdict)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-surface-600 rounded-lg bg-white dark:bg-surface-700 text-gray-900 dark:text-white"
                  >
                    {VERDICTS.map((v) => (
                      <option key={v.value} value={v.value}>{v.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Technology name"
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-surface-600 rounded-lg bg-white dark:bg-surface-700 text-gray-900 dark:text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Slug</label>
                  <input
                    type="text"
                    value={formSlug}
                    onChange={(e) => setFormSlug(e.target.value)}
                    placeholder="Auto from name"
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-surface-600 rounded-lg bg-white dark:bg-surface-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Category</label>
                  <input
                    type="text"
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value)}
                    placeholder="e.g. agent-runtime"
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-surface-600 rounded-lg bg-white dark:bg-surface-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Notes</label>
                <textarea
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-surface-600 rounded-lg bg-white dark:bg-surface-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={formTags}
                  onChange={(e) => setFormTags(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-surface-600 rounded-lg bg-white dark:bg-surface-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowCreate(false)}
                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={saveEval}
                className="px-4 py-1.5 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700"
              >
                {editingEval ? 'Save' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg text-sm font-medium ${
          toast.type === 'success'
            ? 'bg-green-600 text-white'
            : 'bg-red-600 text-white'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  )
}
